import { DEFAULT_YEAR } from '#server/constants';
import { SearchAvailability, SearchFilter, FilterSet } from '#server/models';
import { ActiveFilters } from '@/store';
import { convertActiveFiltersToSearchFilters } from '@/utils';

const getStudyPeriodCodesFromStartDateFilter = (
  activeFilters: ActiveFilters,
  currentFilterSet: FilterSet | null,
): { year: number; studyPeriodCodes?: string[] } => {
  const startDateActiveFilter = activeFilters['Start date'];

  if (!startDateActiveFilter) {
    return { year: DEFAULT_YEAR };
  }

  const startDateFilter = convertActiveFiltersToSearchFilters({ 'Start date': startDateActiveFilter }, currentFilterSet);

  if (startDateFilter.length === 0) {
    return { year: DEFAULT_YEAR };
  }

  let filterWithLowestNestedYear: SearchFilter | undefined;

  if (startDateFilter.length === 1) {
    filterWithLowestNestedYear = startDateFilter[0];
  } else {
    startDateFilter.forEach((filter) => {
      if (!filterWithLowestNestedYear) {
        filterWithLowestNestedYear = filter;
        return;
      }

      const year = filter.nested!.filters[1]!.valueExact;

      if (year && year < filterWithLowestNestedYear!.nested!.filters[1]!.valueExact!) {
        filterWithLowestNestedYear = filter;
      }
    });
  }

  const studyPeriodFilter = filterWithLowestNestedYear!.nested!.filters[0]!;
  const yearFilter = filterWithLowestNestedYear!.nested!.filters[1]!;

  const studyPeriodCodes =
    studyPeriodFilter.valueIn ?? (studyPeriodFilter.valueExact ? [studyPeriodFilter.valueExact] : undefined);

  return {
    year: Number(yearFilter.valueExact!),
    studyPeriodCodes,
  };
};

const getDeliveryModesFromAttendanceModeFilter = (
  activeFilters: ActiveFilters,
  currentFilterSet: FilterSet | null,
): string[] | undefined => {
  const selectedTitles = activeFilters['Attendance mode'];
  if (!selectedTitles || selectedTitles.length === 0) return undefined;

  const filterDef = currentFilterSet?.find((f) => f.title === 'Attendance mode');
  if (!filterDef?.options) return undefined;

  const titleToValue = new Map(filterDef.options.map((o) => [o.title, o.value]));

  const deliveryModes = selectedTitles.flatMap((title) => {
    const value = titleToValue.get(title);
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  });

  return deliveryModes.length > 0 ? deliveryModes : undefined;
};

export const calculateAvailability = (
  activeFilters: ActiveFilters,
  currentFilterSet: FilterSet | null,
): SearchAvailability => {
  const { year, studyPeriodCodes } = getStudyPeriodCodesFromStartDateFilter(activeFilters, currentFilterSet);
  const deliveryModes = getDeliveryModesFromAttendanceModeFilter(activeFilters, currentFilterSet);

  return {
    year,
    ...(studyPeriodCodes ? { studyPeriodCodes } : {}),
    ...(deliveryModes ? { deliveryModes } : {}),
  };
};
